/** Credential fields returned by API but not shown in connect UI. */
export const BINANCE_IGNORED_CREDENTIAL_FIELDS = new Set([
  "binance_spot_base_url",
]);

export const BINANCE_FIELD_LABELS: Record<string, string> = {
  binance_api_key: "API Key",
  binance_api_secret: "API Secret",
  binance_spot_symbols: "Trading pairs",
  binance_account_start_date: "Account start date",
};

export const BINANCE_FIELD_HINTS: Record<string, string> = {
  binance_api_key: "Use a key with read permissions only.",
  binance_api_secret: "Secret is encrypted and not exposed after save.",
  binance_spot_symbols:
    "List every USDT pair you have traded; sync only fetches trades for these symbols.",
  binance_account_start_date:
    "Leave empty to import all trade history for these pairs.",
};

/** Account start date helper when Binance `trade_sync` is present (portfolio inception only). */
export function getBinanceAccountStartDateHint(
  lookbackDays: number,
  fromDate: string,
  toDate: string
): string {
  return `Optional. Sets portfolio analytics inception only — API sync still covers the last ${lookbackDays} days (${fromDate} through ${toDate}). Leave empty where supported.`;
}

export function isBinanceSpotSymbolsField(field: string): boolean {
  return field === "binance_spot_symbols";
}

export function isBinanceAccountStartDateField(field: string): boolean {
  return field === "binance_account_start_date";
}

export function isBinanceSyncCredentialField(field: string): boolean {
  return isBinanceSpotSymbolsField(field) || isBinanceAccountStartDateField(field);
}

export function isBinanceApiKeyField(field: string): boolean {
  return field === "binance_api_key";
}

export function isBinanceApiSecretField(field: string): boolean {
  return field === "binance_api_secret";
}

export function isBrokerSecretCredentialField(field: string): boolean {
  const lower = field.toLowerCase();
  return (
    isBinanceApiSecretField(field) || lower.includes("secret") || lower.includes("password")
  );
}

export function isBrokerApiKeyCredentialField(field: string): boolean {
  const lower = field.toLowerCase();
  return isBinanceApiKeyField(field) || lower.includes("api_key");
}

export function isBrokerAccessTokenCredentialField(field: string): boolean {
  return field.toLowerCase().includes("access_token");
}

/** API keys/secrets/tokens — masked text inputs (not password fields) and excluded from autofill. */
export function isBrokerMaskedCredentialField(field: string): boolean {
  return (
    isBrokerApiKeyCredentialField(field) ||
    isBrokerSecretCredentialField(field) ||
    isBrokerAccessTokenCredentialField(field)
  );
}

/** Block browser/password-manager autofill on broker credential inputs. */
export function getBrokerCredentialInputAttributes(field: string) {
  const attrs = {
    name: `broker-credential-${field}-no-autofill`,
    autoComplete: "off" as const,
    autoCorrect: "off" as const,
    autoCapitalize: "off" as const,
    spellCheck: false,
    "data-lpignore": "true",
    "data-1p-ignore": "true",
    "data-form-type": "other",
  };

  if (!isBrokerMaskedCredentialField(field)) {
    return attrs;
  }

  return {
    ...attrs,
    "data-broker-masked-credential": "",
  };
}

export type BinanceSecretsOnFile = {
  hasApiKeyOnFile: boolean;
  hasApiSecretOnFile: boolean;
};

/** Keys only — used before sync requirements are filled (test connection). */
export function validateBinanceAuthCredentials(
  values: Record<string, string>,
  secretsOnFile?: BinanceSecretsOnFile
): { ok: true } | { ok: false; message: string } {
  const key = String(values.binance_api_key || "").trim();
  const secret = String(values.binance_api_secret || "").trim();
  if (!key && !secretsOnFile?.hasApiKeyOnFile) {
    return { ok: false, message: "API Key is required." };
  }
  if (!secret && !secretsOnFile?.hasApiSecretOnFile) {
    return { ok: false, message: "API Secret is required." };
  }
  return { ok: true };
}

export function normalizeBinanceSpotSymbol(symbol: string): string {
  return String(symbol || "")
    .trim()
    .replace(/[/\s\-_]/g, "")
    .toUpperCase();
}

/** Parse JSON array or comma-separated pairs → uppercase symbols. */
export function parseBinanceSpotSymbolsInput(raw: string): string[] {
  const trimmed = String(raw || "").trim();
  if (!trimmed) return [];

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const out: string[] = [];
        for (const item of parsed) {
          const sym = normalizeBinanceSpotSymbol(String(item));
          if (sym) out.push(sym);
        }
        return Array.from(new Set(out));
      }
    } catch {
      // fall through to comma split
    }
  }

  const out: string[] = [];
  for (const part of trimmed.split(/[,;\n]+/)) {
    const sym = normalizeBinanceSpotSymbol(part);
    if (sym) out.push(sym);
  }
  return Array.from(new Set(out));
}

export function formatBinanceSpotSymbolsForInput(value: unknown): string {
  if (value == null) return "";
  if (Array.isArray(value)) {
    return value
      .map((s) => normalizeBinanceSpotSymbol(String(s)))
      .filter(Boolean)
      .join(", ");
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[")) {
      try {
        return formatBinanceSpotSymbolsForInput(JSON.parse(trimmed) as unknown);
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return "";
}

/** Validates format when provided; empty is allowed (import all history). */
export function validateBinanceAccountStartDate(ymd: string): string | null {
  const v = String(ymd || "").trim();
  if (!v) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return "Use YYYY-MM-DD format.";
  const d = new Date(`${v}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return "Invalid date.";
  const todayYmd = new Date().toISOString().slice(0, 10);
  if (v > todayYmd) return "Start date cannot be in the future.";
  return null;
}

export function validateBinanceCredentialValues(
  values: Record<string, string>,
  secretsOnFile?: BinanceSecretsOnFile
): { ok: true } | { ok: false; message: string } {
  const authResult = validateBinanceAuthCredentials(values, secretsOnFile);
  if (!authResult.ok) return authResult;
  const symbols = parseBinanceSpotSymbolsInput(values.binance_spot_symbols || "");
  if (symbols.length === 0) {
    return {
      ok: false,
      message: "Add at least one trading pair (e.g. BTCUSDT, ETHUSDT).",
    };
  }
  const startRaw = String(values.binance_account_start_date || "").trim();
  if (startRaw) {
    const dateErr = validateBinanceAccountStartDate(startRaw);
    if (dateErr) return { ok: false, message: dateErr };
  }
  return { ok: true };
}

/** POST /broker/test-connection — keys only; omit empty fields when rotating is not needed. */
export function buildBinanceTestConnectionPayload(
  values: Record<string, string>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const key = String(values.binance_api_key || "").trim();
  const secret = String(values.binance_api_secret || "").trim();
  if (key) payload.binance_api_key = key;
  if (secret) payload.binance_api_secret = secret;
  return payload;
}

/** POST /broker/credentials — full connect payload including sync requirements. */
export function buildBinanceCredentialsPayload(
  values: Record<string, string>,
  secretsOnFile?: BinanceSecretsOnFile
): Record<string, unknown> {
  const symbols = parseBinanceSpotSymbolsInput(values.binance_spot_symbols || "");
  const startDate = String(values.binance_account_start_date || "").trim().slice(0, 10);
  const payload: Record<string, unknown> = {
    binance_spot_symbols: symbols,
  };
  const key = String(values.binance_api_key || "").trim();
  const secret = String(values.binance_api_secret || "").trim();
  if (key) {
    payload.binance_api_key = key;
  } else if (!secretsOnFile?.hasApiKeyOnFile) {
    payload.binance_api_key = "";
  }
  if (secret) {
    payload.binance_api_secret = secret;
  } else if (!secretsOnFile?.hasApiSecretOnFile) {
    payload.binance_api_secret = "";
  }
  if (startDate) {
    payload.binance_account_start_date = startDate;
  }
  return payload;
}

/** Maps GET /broker/credentials/form → form state (no secrets). */
export function binanceCredentialsFormToFormValues(form: Record<string, unknown>): {
  credentialValues: Partial<Record<string, string>>;
  secretsOnFile: BinanceSecretsOnFile;
  accountCode?: string;
} {
  const hasApiKeyOnFile = Boolean(
    form.has_binance_api_key ?? form.hasBinanceApiKey
  );
  const hasApiSecretOnFile = Boolean(
    form.has_binance_api_secret ?? form.hasBinanceApiSecret
  );
  const accountCodeRaw = form.account_code ?? form.accountCode;
  return {
    credentialValues: {
      binance_api_key: "",
      binance_api_secret: "",
      binance_spot_symbols: formatBinanceSpotSymbolsForInput(form.binance_spot_symbols),
      binance_account_start_date:
        form.binance_account_start_date != null &&
        String(form.binance_account_start_date).trim()
          ? String(form.binance_account_start_date).trim().slice(0, 10)
          : "",
    },
    secretsOnFile: { hasApiKeyOnFile, hasApiSecretOnFile },
    accountCode:
      accountCodeRaw != null && String(accountCodeRaw).trim()
        ? String(accountCodeRaw).trim()
        : undefined,
  };
}

export function isBinanceReconnectError(message: string): boolean {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("reconnect") &&
    (m.includes("binance") ||
      m.includes("spot_symbols") ||
      m.includes("account_start_date") ||
      m.includes("binance_spot_symbols"))
  );
}

export function snapshotToBinanceCredentialForm(
  snapshot: Record<string, unknown> | null
): Partial<Record<string, string>> {
  if (!snapshot) return {};
  const symbolsRaw =
    snapshot.binance_spot_symbols ?? snapshot.spot_symbols ?? snapshot.binanceSpotSymbols;
  const startRaw =
    snapshot.binance_account_start_date ??
    snapshot.account_start_date ??
    snapshot.binanceAccountStartDate;
  let startYmd = "";
  if (startRaw != null && String(startRaw).trim()) {
    startYmd = String(startRaw).trim().slice(0, 10);
  }
  let symbolsDisplay = "";
  try {
    symbolsDisplay = formatBinanceSpotSymbolsForInput(symbolsRaw);
  } catch {
    symbolsDisplay = Array.isArray(symbolsRaw)
      ? symbolsRaw.map((s) => String(s)).join(", ")
      : String(symbolsRaw || "");
  }
  return {
    binance_spot_symbols: symbolsDisplay,
    binance_account_start_date: startYmd,
  };
}
